import { generateKeyBetween } from "fractional-indexing";
import * as Y from "yjs";
import { fragmentToJSON, jsonToFragment } from "./richtext.js";

/** Transaction origin for this client's own UI edits (undo manager tracks only these). */
export const LOCAL = "local";

export const CARD_TYPES = [
  "note",
  "heading",
  "image",
  "link",
  "file",
  "video",
  "audio",
  "map",
  "todo",
  "column",
  "board",
  "alias",
  "comment",
  "line",
  "sketch",
  "annotation",
  "swatch",
  "table",
  "document",
  "clone",
] as const;
export type CardType = (typeof CARD_TYPES)[number];

/** Default size in page px when a card of this type is created. Height null = content-driven. */
export const CARD_DEFAULTS: Record<CardType, { w: number; h: number | null; minW: number; minH: number }> = {
  note: { w: 240, h: null, minW: 120, minH: 40 },
  heading: { w: 320, h: null, minW: 80, minH: 30 },
  image: { w: 280, h: 200, minW: 60, minH: 40 },
  link: { w: 280, h: null, minW: 160, minH: 60 },
  file: { w: 240, h: null, minW: 160, minH: 60 },
  video: { w: 360, h: 220, minW: 160, minH: 90 },
  audio: { w: 300, h: 80, minW: 160, minH: 60 },
  map: { w: 360, h: 260, minW: 160, minH: 120 },
  todo: { w: 260, h: null, minW: 160, minH: 60 },
  column: { w: 280, h: null, minW: 180, minH: 80 },
  board: { w: 120, h: 150, minW: 120, minH: 150 },
  alias: { w: 120, h: 150, minW: 120, minH: 150 },
  comment: { w: 260, h: null, minW: 160, minH: 60 },
  line: { w: 0, h: 0, minW: 0, minH: 0 },
  sketch: { w: 320, h: 240, minW: 80, minH: 60 },
  annotation: { w: 800, h: 600, minW: 40, minH: 40 },
  swatch: { w: 120, h: 120, minW: 40, minH: 40 },
  table: { w: 480, h: null, minW: 200, minH: 80 },
  document: { w: 200, h: null, minW: 120, minH: 60 },
  clone: { w: 240, h: null, minW: 120, minH: 40 },
};

export const GRID = 10;
export const CANVAS_SIZE = 200_000;
export const snap = (v: number) => Math.round(v / GRID) * GRID;

export const CARD_COLORS = [
  "white",
  "charcoal",
  "grey",
  "red",
  "orange",
  "yellow",
  "green",
  "cyan",
  "blue",
  "purple",
  "pink",
  "brown",
  "transparent",
] as const;
export type CardColor = (typeof CARD_COLORS)[number];

/** Plain snapshot of a card's scalar fields (rich content stays as Y types). */
export interface CardFields {
  id: string;
  type: CardType;
  x: number;
  y: number;
  w: number;
  h: number | null;
  z: string;
  parent: string | null;
  order: string | null;
  color: CardColor | string;
  colorMode: "background" | "strip";
  locked: boolean;
  labels: string[];
  caption?: string;
  title?: string;
  createdBy: string;
  createdAt: number;
  updatedBy?: string;
  updatedAt?: number;
  // type payloads (optional)
  text?: string;
  fileId?: string | null;
  name?: string;
  mime?: string;
  size?: number;
  aspect?: number;
  url?: string;
  childBoardId?: string;
  targetBoardId?: string;
  syncedKey?: string;
  hex?: string;
  uploading?: boolean;
}

export function getBoardTypes(doc: Y.Doc) {
  return {
    meta: doc.getMap<unknown>("meta"),
    cards: doc.getMap<Y.Map<unknown>>("cards"),
    lines: doc.getMap<Y.Map<unknown>>("lines"),
    /** Shared rich-text bodies of synced notes, keyed by the original note id. */
    synced: doc.getMap<Y.XmlFragment>("synced"),
  };
}

/** Rich-text body of a note/document/clone: its own fragment, or the shared one when the note is synced. */
export function noteFragment(card: Y.Map<unknown>): Y.XmlFragment | null {
  const key = card.get("syncedKey");
  if (typeof key === "string" && card.doc) {
    const shared = getBoardTypes(card.doc).synced.get(key);
    if (shared) return shared;
  }
  const own = card.get("body");
  return own instanceof Y.XmlFragment ? own : null;
}

/** Make a note's body shared so clones can follow it. Returns the synced key. */
export function ensureSynced(doc: Y.Doc, cardId: string): string | null {
  const { cards, synced } = getBoardTypes(doc);
  const card = cards.get(cardId);
  if (!card) return null;
  const existing = card.get("syncedKey");
  if (typeof existing === "string" && synced.has(existing)) return existing;
  const own = card.get("body");
  doc.transact(() => {
    const frag = new Y.XmlFragment();
    synced.set(cardId, frag);
    if (own instanceof Y.XmlFragment) {
      jsonToFragment(fragmentToJSON(own), frag);
    }
    card.set("syncedKey", cardId);
  }, LOCAL);
  return cardId;
}

export const cardFields = (m: Y.Map<unknown>): CardFields => {
  const o: Record<string, unknown> = {};
  m.forEach((v, k) => {
    if (!(v instanceof Y.AbstractType)) o[k] = v;
  });
  return o as unknown as CardFields;
};

/** Fractional key suffix with the client id so concurrent inserts never collide. */
export function orderKey(prev: string | null, next: string | null, clientID: number): string {
  const base = (s: string | null) => (s ? (s.split(":")[0] ?? null) : null);
  return `${generateKeyBetween(base(prev), base(next))}:${clientID.toString(36)}`;
}
export const compareKeys = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0);

export function topZ(cards: Y.Map<Y.Map<unknown>>): string | null {
  let max: string | null = null;
  cards.forEach((c) => {
    const z = c.get("z") as string | undefined;
    if (z && (max === null || z > max)) max = z;
  });
  return max;
}

export interface NewCardInput {
  type: CardType;
  x: number;
  y: number;
  w?: number;
  h?: number | null;
  userId: string;
  id?: string;
  fields?: Partial<CardFields>;
  /** Rich text seed for note/document/comment bodies. */
  body?: Y.XmlFragment;
}

/** Create a card inside a LOCAL transaction. Returns its id. */
export function createCard(doc: Y.Doc, input: NewCardInput): string {
  const { cards } = getBoardTypes(doc);
  const id = input.id ?? crypto.randomUUID();
  const d = CARD_DEFAULTS[input.type];
  doc.transact(() => {
    const card = new Y.Map<unknown>();
    cards.set(id, card);
    card.set("id", id);
    card.set("type", input.type);
    card.set("x", snap(input.x));
    card.set("y", snap(input.y));
    card.set("w", input.w ?? d.w);
    card.set("h", input.h === undefined ? d.h : input.h);
    card.set("z", orderKey(topZ(cards), null, doc.clientID));
    card.set("parent", null);
    card.set("order", null);
    card.set("color", input.type === "heading" || input.type === "board" ? "transparent" : "charcoal");
    card.set("colorMode", "background");
    card.set("locked", false);
    card.set("labels", []);
    card.set("createdBy", input.userId);
    card.set("createdAt", Date.now());
    if (input.fields) for (const [k, v] of Object.entries(input.fields)) if (v !== undefined) card.set(k, v);
    if (input.type === "note" || input.type === "document" || input.type === "comment")
      card.set("body", input.body ?? new Y.XmlFragment());
    if (input.type === "heading" && !card.has("text")) card.set("text", "");
    if (input.type === "todo") {
      card.set("title", "");
      card.set("items", new Y.Array());
    }
    if (input.type === "comment") {
      card.set("messages", new Y.Array());
      card.set("collapsed", false);
    }
    if (input.type === "sketch" || input.type === "annotation") card.set("strokes", new Y.Array());
    if (input.type === "column") card.set("title", "");
    if (input.type === "table") {
      card.set("title", "");
      card.set("rows", 3);
      card.set("cols", 3);
      card.set("cells", new Y.Map());
    }
  }, LOCAL);
  return id;
}

export function bringToFront(doc: Y.Doc, ids: string[]) {
  const { cards } = getBoardTypes(doc);
  doc.transact(() => {
    let z = topZ(cards);
    for (const id of ids) {
      const c = cards.get(id);
      if (!c) continue;
      z = orderKey(z, null, doc.clientID);
      c.set("z", z);
    }
  }, LOCAL);
}

export function sendToBack(doc: Y.Doc, ids: string[]) {
  const { cards } = getBoardTypes(doc);
  doc.transact(() => {
    let min: string | null = null;
    cards.forEach((c) => {
      const z = c.get("z") as string | undefined;
      if (z && (min === null || z < min)) min = z;
    });
    for (const id of [...ids].reverse()) {
      const c = cards.get(id);
      if (!c) continue;
      min = orderKey(null, min, doc.clientID);
      c.set("z", min);
    }
  }, LOCAL);
}

export interface LineEnd {
  cardId: string | null;
  x: number;
  y: number;
  side?: "top" | "right" | "bottom" | "left" | null;
}
export interface LineFields {
  id: string;
  from: LineEnd;
  to: LineEnd;
  color: string;
  weight: "thin" | "thick";
  dashed: boolean;
  startHead: "none" | "arrow";
  endHead: "none" | "arrow";
  label: string;
  curve: { cx: number; cy: number } | null;
}

export function createLine(
  doc: Y.Doc,
  from: LineEnd,
  to: LineEnd,
  userId: string,
  fields: Partial<LineFields> = {},
): string {
  const { lines } = getBoardTypes(doc);
  const id = crypto.randomUUID();
  doc.transact(() => {
    const l = new Y.Map<unknown>();
    lines.set(id, l);
    l.set("id", id);
    l.set("from", from);
    l.set("to", to);
    l.set("color", fields.color ?? "#b3b3b3");
    l.set("weight", fields.weight ?? "thin");
    l.set("dashed", fields.dashed ?? false);
    l.set("startHead", fields.startHead ?? "none");
    l.set("endHead", fields.endHead ?? "arrow");
    l.set("label", fields.label ?? "");
    l.set("curve", fields.curve ?? null);
    l.set("createdBy", userId);
    l.set("createdAt", Date.now());
  }, LOCAL);
  return id;
}

/** Remove lines whose endpoints reference deleted cards. */
export function pruneLines(doc: Y.Doc) {
  const { cards, lines } = getBoardTypes(doc);
  const dead: string[] = [];
  lines.forEach((l, id) => {
    const f = l.get("from") as LineEnd;
    const t = l.get("to") as LineEnd;
    if ((f.cardId && !cards.has(f.cardId)) || (t.cardId && !cards.has(t.cardId))) dead.push(id);
  });
  if (dead.length) doc.transact(() => dead.forEach((id) => lines.delete(id)), LOCAL);
}
